<script setup lang="ts">
// Public directory of contracting-unit purchasing contacts, from the
// precomputed procurement_contacts rollup. Public data (comprasestatales).
// Insightful filters (organism group + deliverability) + a four-format download,
// so people who need to reach a buying unit can find and export the right contact.
const { t, locale } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()

const search = ref((route.query.q as string) ?? '')
const grupo = ref((route.query.grupo as string) ?? '')
const hasEmail = ref(route.query.hasEmail === '1')
const hasPhone = ref(route.query.hasPhone === '1')
const sort = ref((route.query.sort as string) ?? 'calls')
const page = ref(Number(route.query.page ?? 1) || 1)
const searchDebounced = refDebounced(search, 350)
const searchTerm = computed(() => searchDebounced.value.trim())

const FORMATS = [
  { fmt: 'csv', icon: 'mdi-file-delimited-outline' },
  { fmt: 'xlsx', icon: 'mdi-file-excel-outline' },
  { fmt: 'json', icon: 'mdi-code-json' },
  { fmt: 'vcf', icon: 'mdi-card-account-details-outline' },
] as const

const SORTS: Record<string, { sortBy: string }> = {
  calls: { sortBy: 'llamados' },
  name: { sortBy: 'organism' },
}

// The filter half of the query (everything the export also honours).
const filterQuery = computed(() => ({
  ...(searchTerm.value ? { q: searchTerm.value } : {}),
  ...(grupo.value ? { grupo: grupo.value } : {}),
  ...(hasEmail.value ? { hasEmail: '1' } : {}),
  ...(hasPhone.value ? { hasPhone: '1' } : {}),
  ...(SORTS[sort.value] ?? SORTS.calls),
}))
const listQuery = computed(() => ({ page: page.value, limit: 25, ...filterQuery.value }))

const hasFilters = computed(() => !!grupo.value || hasEmail.value || hasPhone.value)
function clearFilters() {
  search.value = ''
  grupo.value = ''
  hasEmail.value = false
  hasPhone.value = false
  page.value = 1
}

watch([searchTerm, grupo, hasEmail, hasPhone, sort], () => {
  page.value = 1
})
watch([searchTerm, grupo, hasEmail, hasPhone, sort, page], () => {
  router.replace({ query: {
    ...(searchTerm.value ? { q: searchTerm.value } : {}),
    ...(grupo.value ? { grupo: grupo.value } : {}),
    ...(hasEmail.value ? { hasEmail: '1' } : {}),
    ...(hasPhone.value ? { hasPhone: '1' } : {}),
    ...(sort.value !== 'calls' ? { sort: sort.value } : {}),
    ...(page.value > 1 ? { page: String(page.value) } : {}),
  } })
})

const { data: listRes, pending, error } = await useFetch<any>('/api/contactos', { query: listQuery })
const { data: totalRes } = await useFetch<any>('/api/contactos', { query: { limit: 1 }, key: 'contactos-total' })

const contacts = computed<any[]>(() => listRes.value?.data?.contacts ?? [])
const groups = computed<any[]>(() => listRes.value?.data?.groups ?? [])
const pagination = computed(() => listRes.value?.data?.pagination ?? null)
const directoryTotal = computed<number | null>(() => totalRes.value?.data?.pagination?.total ?? null)
const totalPages = computed(() => Math.max(1, pagination.value?.totalPages ?? 1))

const contactColumns = computed(() => [
  { key: 'organismName', label: t('contactos.colOrganism'), primary: true },
  { key: 'group', label: t('contactos.colGroup') },
  { key: 'contactName', label: t('contactos.colContact') },
  { key: 'emails', label: t('contactos.colEmail') },
  { key: 'telephone', label: t('contactos.colPhone'), mono: true },
  { key: 'llamadosCount', label: t('contactos.colCalls'), align: 'end' as const, mono: true },
])

const GROUP_ITEMS = computed(() => [
  { title: t('contactos.filter.groupAny'), value: '' },
  ...groups.value.map(g => ({ title: locale.value === 'en' ? g.labelEn : g.label, value: g.key })),
])

function telHref(v?: string) {
  const d = (v ?? '').replace(/[^\d+]/g, '')
  return d ? `tel:${d}` : ''
}
function exportUrl(fmt: string) {
  const params = new URLSearchParams(Object.entries(filterQuery.value).map(([k, v]) => [k, String(v)]))
  params.set('format', fmt)
  return `/api/contactos/export?${params.toString()}`
}

const orgLd = useOrgLd()
useSeo(() => {
  const title = t('seo.contactos.title')
  const description = t('seo.contactos.description', { total: formatNumber(directoryTotal.value) })
  return {
    title,
    description,
    path: '/contactos',
    noindex: Boolean(searchTerm.value) || hasFilters.value,
    kicker: 'Contactos',
    jsonLd: [
      { '@context': 'https://schema.org', '@type': 'CollectionPage', 'name': title, 'description': description },
      orgLd,
    ],
  }
})
</script>

<template>
  <div class="u-container page">
    <header class="page__head">
      <p class="u-eyebrow">
        {{ t('home.eyebrow') }}
      </p>
      <h1>{{ t('contactos.title') }}</h1>
      <p class="u-lead page__lead">
        {{ t('contactos.lead') }}
      </p>
    </header>

    <div class="toolbar">
      <form
        class="find"
        role="search"
        @submit.prevent
      >
        <label
          class="u-sr-only"
          for="contact-q"
        >{{ t('common.search') }}</label>
        <v-icon
          class="find__icon"
          size="20"
        >
          mdi-magnify
        </v-icon>
        <input
          id="contact-q"
          v-model="search"
          class="find__input"
          type="search"
          :placeholder="t('contactos.searchPlaceholder')"
        >
        <button
          v-if="search"
          class="find__x"
          type="button"
          :aria-label="t('common.clear')"
          @click="() => (search = '')"
        >
          <v-icon size="18">
            mdi-close
          </v-icon>
        </button>
      </form>
    </div>

    <!-- ===== Filters ===== -->
    <div class="filters">
      <label class="filters__sel">
        <span class="u-sr-only">{{ t('contactos.filter.group') }}</span>
        <select
          v-model="grupo"
          class="sel"
        >
          <option
            v-for="it in GROUP_ITEMS"
            :key="it.value"
            :value="it.value"
          >
            {{ it.title }}
          </option>
        </select>
      </label>

      <label class="filters__sel">
        <span class="u-sr-only">{{ t('contactos.filter.sort') }}</span>
        <select
          v-model="sort"
          class="sel"
        >
          <option value="calls">
            {{ t('contactos.filter.sortCalls') }}
          </option>
          <option value="name">
            {{ t('contactos.filter.sortName') }}
          </option>
        </select>
      </label>

      <label class="filters__chk">
        <input
          v-model="hasEmail"
          type="checkbox"
        >
        <span>{{ t('contactos.filter.hasEmail') }}</span>
      </label>
      <label class="filters__chk">
        <input
          v-model="hasPhone"
          type="checkbox"
        >
        <span>{{ t('contactos.filter.hasPhone') }}</span>
      </label>

      <button
        v-if="hasFilters"
        class="filters__clear"
        type="button"
        @click="clearFilters"
      >
        {{ t('common.clear') }}
      </button>

      <div class="dl">
        <span class="dl__label">{{ t('contactos.download') }}</span>
        <a
          v-for="f in FORMATS"
          :key="f.fmt"
          class="dl__btn"
          :href="exportUrl(f.fmt)"
          :title="f.fmt.toUpperCase()"
          rel="nofollow"
        >
          <v-icon size="18">{{ f.icon }}</v-icon>
          <span class="dl__ext">{{ f.fmt }}</span>
        </a>
      </div>
    </div>

    <p
      v-if="pagination?.total != null"
      class="count"
    >
      {{ t('contactos.resultsSummary', { count: formatNumber(pagination.total) }) }}
    </p>

    <PaginatedList
      v-model:page="page"
      :total-pages="totalPages"
    >
      <StatePanel
        v-if="error"
        :title="t('errors.generic.title')"
        :body="t('errors.generic.body')"
        :action-label="t('errors.generic.action')"
        @action="() => refreshNuxtData()"
      />

      <SkeletonList
        v-else-if="pending && !contacts.length"
        :rows="8"
      />

      <StatePanel
        v-else-if="!contacts.length"
        :title="t('contactos.empty.title')"
        :body="t('contactos.empty.body')"
        :action-label="searchTerm || hasFilters ? t('contactos.empty.action') : undefined"
        @action="clearFilters"
      />

      <DataTable
        v-else
        :columns="contactColumns"
        :rows="contacts"
        :row-key="contact => contact.organismId"
        min-width="760px"
      >
        <template #cell:organismName="{ row }">
          <NuxtLink
            :to="localePath(`/buyers/${encodeURIComponent(row.organismId)}`)"
            class="ctable__link"
          >
            <span class="u-truncate">{{ row.organismName }}</span>
          </NuxtLink>
        </template>
        <template #cell:group="{ row }">
          <span
            v-if="row.group"
            class="grouptag"
          >{{ row.group }}</span>
          <span
            v-else
            class="u-muted"
          >—</span>
        </template>
        <template #cell:contactName="{ row }">
          {{ row.contactName || '—' }}
        </template>
        <template #cell:emails="{ row }">
          <div
            v-if="row.emails && row.emails.length"
            class="emails"
          >
            <a
              v-for="email in row.emails"
              :key="email"
              :href="`mailto:${email}`"
              class="ctable__link"
            >
              {{ email }}
            </a>
          </div>
          <span
            v-else
            class="u-muted"
          >—</span>
        </template>
        <template #cell:telephone="{ row }">
          <a
            v-if="row.telephone && telHref(row.telephone)"
            :href="telHref(row.telephone)"
            class="ctable__link"
          >{{ row.telephone }}</a>
          <span v-else>{{ row.telephone || '—' }}</span>
        </template>
        <template #cell:llamadosCount="{ row }">
          {{ formatNumber(row.llamadosCount) }}
        </template>
      </DataTable>
    </PaginatedList>

    <p class="u-muted directory__note">
      {{ t('contactos.note') }}
    </p>
  </div>
</template>

<style scoped>
.count { color: var(--text-muted); font-size: var(--t-sm); margin: var(--s-3) 0; }
.filters { display: flex; flex-wrap: wrap; gap: var(--s-2); align-items: center; margin: var(--s-3) 0; }
.sel { padding: 8px 10px; border-radius: 8px; border: 1px solid var(--rule); background: var(--surface, transparent); color: inherit; max-width: 260px; }
.filters__chk { display: inline-flex; align-items: center; gap: 6px; font-size: var(--t-sm); color: var(--text-muted); cursor: pointer; }
.filters__clear { background: none; border: 1px solid var(--rule); border-radius: 8px; padding: 7px 12px; color: inherit; cursor: pointer; font-size: var(--t-sm); }
.dl { display: inline-flex; align-items: center; gap: 6px; margin-left: auto; }
.dl__label { font-size: var(--t-xs); color: var(--text-muted); }
.dl__btn { display: inline-flex; align-items: center; gap: 4px; padding: 6px 9px; border: 1px solid var(--rule); border-radius: 8px; color: var(--celeste-deep); text-decoration: none; font-size: var(--t-xs); }
.dl__btn:hover { border-color: var(--celeste-deep); }
.dl__ext { text-transform: uppercase; }
.ctable__link { color: var(--celeste-deep); text-decoration: none; display: inline-block; max-width: 100%; }
.ctable__link:hover { text-decoration: underline; }
.emails { display: flex; flex-direction: column; gap: 2px; }
.grouptag { font-size: 0.78rem; padding: 2px 8px; border-radius: 999px; border: 1px solid var(--rule); white-space: nowrap; color: var(--text-muted); }
.directory__note { font-size: var(--t-xs); margin-top: var(--s-4); }
</style>
